/* This module exports several functions related to the
information on a checkout page -- who to ship to, contact name,
billing name, etc.

Checkout data has the following fields:

  shipping: address
  billing: address
  billing_different_address: bool

*/

export const ADDRESS_TYPE_SHIPPING = 'shipping';
export const ADDRESS_TYPE_VERIFY = 'verify';

export const USA = {
  name: "United States",
  code: "US",
  url: "/services/cart/api/v1/cart/countries/US/",
}

/**
 * Check that provided country is USA.
 *
 * @param {string} country
 * @returns {boolean}
 */
export function isUSA(country) {
  const isString = typeof country === "string";
  return isString && (country === USA.code || country.endsWith(USA.url));
}


/**
 * Return the tax/shipping nexus data. Basically, this should be all
 * the information needed to calculate tax rate. Therefore if this
 * returns value, the tax rate API call can be made, and if the return
 * value changes, then a new tax rate call can be made.
 * @param {*} checkoutData
 */
export function nexus({shipping}) {
  if (!shipping) return;
  let {country, state, postcode} = shipping;
  if (!country || (isUSA(country) && !state) || !postcode) return;
  if (!postcodeComplete(country, postcode)) return;
  return {country, state, postcode};
}

/**
 * Compare sameness for nexus. a or b can be null, or an object with country and postcode.
 * @param {*} a
 * @param {*} b
 */
export function nexusEqual(a, b) {
  return (!a && !b) || (a && b && a.country === b.country && a.postcode === b.postcode);
}

/**
 * Return whether the postcode is complete, ie we should be able
 * to figure out shipping / tax.
 * NOTE: Only US postcode is checked here.
 */
export function postcodeComplete(country, postcode) {
  if (isUSA(country)) {
    return /^[0-9]{5}(?:-[0-9]{4})?/.test(postcode)
  }
  return country && postcode;
}

/**
 * Validate whether the checkout form is complete enough to allow checkout
 * @param {*} param0
 */
export function validateInfo({checkout, payment, needsPayment}, issues) {
  let why = [];
  if (!isAddressComplete(checkout.shipping, 'shipping')) why.push('Shipping address not complete');
  if (!isAddressComplete(getBillingAddress(checkout), 'billing')) why.push('Billing address not complete');
  if (needsPayment && !isPaymentComplete(payment)) why.push('Payment info not complete');
  if (issues) issues.push(...why);
  return why.length === 0;
}

/**
 * Return the billing address for checkout, either a copy of the shipping address of not.
 * @param {*} checkout
 */
export function getBillingAddress(checkout) {
  if (checkout.billing_different_address) {
    return checkout.billing;
  } else {
    return Object.assign({}, checkout.shipping);
  }
}

export function isAddressComplete(address, address_type) {
  // ADDRESS_TYPE_VERIFY only needs the address fields
  // ADDRESS_TYPE_SHIPPING needs address fields, name, email and phone number
  // Other addresses need name, and address fields
  return (address_type === ADDRESS_TYPE_VERIFY || address.first_name)
      && (address_type === ADDRESS_TYPE_VERIFY || address.last_name)
      && (address_type !== ADDRESS_TYPE_SHIPPING || address.email_address)
      && (address_type !== ADDRESS_TYPE_SHIPPING || address.phone_number)
      && address.line1
      && address.city
      && (!isUSA(address.country) || address.state)
      && postcodeComplete(address.country, address.postcode);
}

export function isPaymentComplete(payment) {
  return payment.saved_card || (payment.card_number && payment.expiration && payment.cvc && saveInfoComplete(payment));
}

function saveInfoComplete(payment) {
  // The "title" is required when saving a card
  return !payment.save_card || payment.title;
}

export function expandCheckoutInfo(checkout) {
  let {billing, ...rest} = checkout;
  return {billing: getBillingAddress(checkout), ...rest};
}

export function empty_address() {
  return {
    country: USA.code,
    first_name: "",
    last_name: "",
    line1: "",
    line2: "",
    notes: "",
    postcode: "",
    state: "",
    city: "",
    phone_number: "",
    email_address: ""
  };
}

export function empty_payment() {
  return {
    card_number: '',
    expiration: '',
    cvc: '',
    save_card: false
  }
}



// WEBPACK FOOTER //
// ./src/lib/CheckoutLogic.js